var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { html, KompisElement, property } from '@inter-ikea-kompis/web-component';
import { DateFormatter, DexfSettingsUtility } from '@inter-ikea-kompis/utilities';
import { DesignCard, DesignCardSizeEnum } from '@inter-ikea-kompis/component-design-card';
import { OpenDesignCode, OpenDesignCodeInput, OpenDesignCodeStateEnum } from '@inter-ikea-kompis/component-open-design-code';
import { Text } from '@ingka/common-styles-webc';
import { informationCircle, list, registerIcon, squareGrid2x2 } from '@ingka/icon-store';
import { Icon as SkapaIcon } from '@ingka/icon-webc/passive.js';
import { Image as SkapaImage } from '@ingka/image-webc/passive.js';
import { InlineMessage as SkapaInlineMessage } from '@ingka/inline-message-webc/passive.js';
import { Tabs as SkapaTabs } from '@ingka/tabs-webc/passive.js';
import { Toggle as SkapaToggle } from '@ingka/toggle-webc/passive.js';
import OpenDesignStateEnum from '../../enums/OpenDesignStateEnum.js';
import OpenDesignTabEnum from '../../enums/OpenDesignTabEnum.js';
import OpenDesignVariantEnum from '../../enums/OpenDesignVariantEnum.js';
import OpenDesignCardStyle from './OpenDesignCardStyle.js';
/**
 * @event tabSelect Triggered when a tab is selected.
 * @event openDesignCode Triggered when enter is pressed in the design code input.
 * @event designCodeInput Triggered when the user writes in the design code input.
 * @event designSelect Triggered when a saved design is selected.
 * @event resetOpenDesignCodeState Conditionally triggered when a tab is selected.
 */
export default class OpenDesignCard extends KompisElement {
    constructor() {
        super(...arguments);
        // Localized information. Deprecated - use "translations" and "settings" instead.
        this.localizedInformation = null;
        // The translations retrieved from translations service.
        this.translations = null;
        // The settings retrieved from settings service.
        this.settings = null;
        // Whether the user is logged in to IKEA or not. Always set to false when in Kiosk.
        this.loggedIn = false;
        // The state of open design code.
        this.openDesignCodeState = OpenDesignCodeStateEnum.default;
        // List of saved designs retrieved from save service.
        this.savedDesigns = null;
        // The state of open saved design.
        this.openSavedDesignState = OpenDesignStateEnum.default;
        // Which variant of the component to show. Currently open design (default) or import design.
        this.variant = OpenDesignVariantEnum.openDesign;
        // Message to be displayed in an inline message, e.g. "Currently only available for PAX wardrobe designs".
        this.helpMessage = null;
        // Whether open design card should display tabs.
        this.enableTabs = false;
        // The tab that OpenDesignCard should display
        this.selectedTab = OpenDesignTabEnum.ikeaProfile;
        this.cardSize = DesignCardSizeEnum.big;
        this.dexfSettings = null;
        this.lastClickedDesignId = null;
    }
    render() {
        // We want translations to be required, but since both props to provide translations are optional, this check is needed until localizedInformation is removed.
        if (!this.hasTranslationsAndSettings()) {
            // We can't throw an error because on the first render no props are set.
            return null;
        }
        return html `
			<style>
				${OpenDesignCardStyle.getStyle({
            theme: this.theme
        })}
			</style>

			<div class="header">
				<h2 id="heading" class="${Text.classnames({ heading: { m: true } })}">
					${this.variant === OpenDesignVariantEnum.openDesign
            ? this.getTranslations().openDesignHeader
            : this.getTranslations().importDesignHeader}
				</h2>
				${this.selectedTab === OpenDesignTabEnum.ikeaProfile
            ? html `<skapa-toggle
							.activeIndex="${this.cardSize === DesignCardSizeEnum.big ? 0 : 1}"
							@skapa-toggle-change="${this.onCardSizeChange.bind(this)}"
						>
							<button aria-label="${this.getTranslations().openDesignArialabelGridLayout}">
								<skapa-icon icon="square-grid-2x2"></skapa-icon>
							</button>
							<button aria-label="${this.getTranslations().openDesignArialabelListLayout}">
								<skapa-icon icon="list"></skapa-icon>
							</button>
						</skapa-toggle>`
            : ''}
			</div>

			${this.enableTabs ? this.renderWithTabs() : this.renderWithoutTabs()}
		`;
    }
    async connectedCallback() {
        registerIcon(informationCircle);
        registerIcon(list);
        registerIcon(squareGrid2x2);
        this.customElements.define('skapa-inline-message', SkapaInlineMessage);
        this.customElements.define('skapa-icon', SkapaIcon);
        this.customElements.define('skapa-image', SkapaImage);
        this.customElements.define('skapa-tabs', SkapaTabs);
        this.customElements.define('skapa-toggle', SkapaToggle);
        this.customElements.define('kompis-design-card', DesignCard);
        this.customElements.define('kompis-open-design-code', OpenDesignCode);
        this.customElements.define('kompis-open-design-code-input', OpenDesignCodeInput);
        super.connectedCallback();
    }
    shouldUpdate(changedProperties) {
        if (changedProperties.has('localizedInformation') &&
            this.localizedInformation &&
            !this.settings) {
            this.dexfSettings = DexfSettingsUtility.parseLocalizedInformation(this.localizedInformation);
        }
        if (changedProperties.has('settings') && this.settings) {
            this.dexfSettings = this.settings;
        }
        if (changedProperties.has('selectedTab')) {
            const tabsElement = this.shadowRoot.querySelector('[data-element-name="skapa-tabs"]');
            const selectedTabNr = this.selectedTab === OpenDesignTabEnum.ikeaProfile ? 0 : 1;
            if (tabsElement) {
                tabsElement.activeTab = `tabs-tab-${selectedTabNr}`;
            }
        }
        return true;
    }
    hasTranslationsAndSettings() {
        return (!!this.translations && !!this.settings) || !!this.localizedInformation?.translations;
    }
    getTranslations() {
        const translations = this.translations
            ? this.translations
            : this.localizedInformation?.translations;
        return translations;
    }
    getSettings() {
        return this.dexfSettings;
    }
    renderWithTabs() {
        const { openDesignHeader, myDesigns, openDesignDesignCodeTitle } = this.getTranslations();
        return html `${this.getHelpMessage(true)}
			<skapa-tabs
				auto-id="tabs"
				tablist-label="${openDesignHeader}"
				@skapa-tab-select="${this.onTabSelect.bind(this)}"
			>
				<button>${myDesigns}</button>
				<div slot="panels">
					${this.loggedIn ? this.getSavedDesigns() : this.getLogInInlineMessage()}
				</div>

				<button>${openDesignDesignCodeTitle}</button>
				<div slot="panels">${this.getOpenDesignCode()}</div>
			</skapa-tabs>`;
    }
    renderWithoutTabs() {
        if (this.selectedTab === OpenDesignTabEnum.ikeaProfile) {
            if (this.loggedIn) {
                return this.getSavedDesigns();
            }
            else {
                return this.getLogInInlineMessage();
            }
        }
        else {
            return this.getOpenDesignCodeWithHelpMessage();
        }
    }
    onTabSelect(event) {
        event.preventDefault();
        const selectedTab = event.detail === 1 ? OpenDesignTabEnum.designCode : OpenDesignTabEnum.ikeaProfile;
        this.dispatchEvent(new CustomEvent('tabSelect', {
            bubbles: true,
            detail: { selectedTab }
        }));
        this.requestUpdate();
        if (selectedTab !== OpenDesignTabEnum.designCode &&
            this.openDesignCodeState === OpenDesignCodeStateEnum.failed) {
            this.dispatchEvent(new CustomEvent('resetOpenDesignCodeState', {
                bubbles: true,
                detail: { openDesignCodeState: OpenDesignCodeStateEnum.default }
            }));
        }
    }
    getLogInInlineMessage() {
        return html `<skapa-inline-message subtle>
			<skapa-icon slot="message-icon" icon="information-circle"></skapa-icon>
			<span slot="body"
				>${this.variant === OpenDesignVariantEnum.openDesign
            ? this.getTranslations().openDesignLogInMessage
            : this.getTranslations().importDesignLogInMessage}</span
			>
		</skapa-inline-message>`;
    }
    getSavedDesigns() {
        if (!this.savedDesigns?.length) {
            return this.getNoSavedDesignsInlineMessage();
        }
        const sortedSavedDesigns = [...(this.savedDesigns ?? [])].sort((a, b) => {
            const latestVersionOfA = a.versions[0];
            const latestVersionOfB = b.versions[0];
            return Date.parse(latestVersionOfB.savedDate) - Date.parse(latestVersionOfA.savedDate);
        });
        const listItems = sortedSavedDesigns.map((savedDesign) => {
            const latestVersionOfSavedDesign = savedDesign.versions[0];
            const description = savedDesign.description;
            const { imageUrl, designId, savedDate } = latestVersionOfSavedDesign;
            return html `<kompis-design-card
				.loading="${this.openSavedDesignState === OpenDesignStateEnum.loading &&
                designId === this.lastClickedDesignId}"
				@click="${() => {
                this.lastClickedDesignId = designId;
                this.dispatchEvent(new CustomEvent('designSelect', {
                    bubbles: true,
                    detail: { designId }
                }));
            }}"
				.size="${this.cardSize}"
				imageAltText="${description}"
				imageUrl="${imageUrl}"
				title="${this.getFormattedDate(savedDate)}"
				subtitle="${description}"
			></kompis-design-card>`;
        });
        const ariaLabel = this.getTranslations().openDesignAriaLabelSavedDesigns;
        return html `<div class="design-items" role="list" aria-label="${ariaLabel}">${listItems}</div>`;
    }
    getNoSavedDesignsInlineMessage() {
        return html `<p
			class="${Text.classnames({ body: { m: true } })} ${this.enableTabs
            ? 'text-center'
            : 'centered'}"
		>
			${this.getTranslations().openDesignNoSavedDesignsMessage}
		</p>`;
    }
    getFormattedDate(savedDate) {
        const format = `${this.getSettings().localisation.dateFormat} HH:mm`;
        return DateFormatter.dateToString(savedDate, format);
    }
    getOpenDesignCodeWithHelpMessage() {
        return html `${this.getHelpMessage(false)}${this.getOpenDesignCode()}`;
    }
    getOpenDesignCode() {
        return html `<kompis-open-design-code-input
				.theme="${this.theme}"
				.localizedInformation="${this.localizedInformation}"
				.translations="${this.translations}"
				.state="${this.openDesignCodeState}"
				@input="${(event) => this.dispatchEvent(new CustomEvent('designCodeInput', {
            bubbles: true,
            detail: {
                designCode: event.detail.vpcCode
            }
        }))}"
				@open="${(event) => this.dispatchEvent(new CustomEvent('openDesignCode', {
            bubbles: true,
            detail: {
                designCode: event.detail.vpcCode
            }
        }))}"
			></kompis-open-design-code-input>
			${this.getInlineMessage()}`;
    }
    getInlineMessage() {
        return html `<div aria-live="assertive">${this.getUnavailableDesignErrorMessage()}</div>`;
    }
    getUnavailableDesignErrorMessage() {
        if (this.openDesignCodeState !== OpenDesignCodeStateEnum.failed) {
            return null;
        }
        const error = this.getTranslations().invalidConfigurationMessage;
        const cautionaryVariant = 'cautionary';
        return html `
			<skapa-inline-message subtle variant="${cautionaryVariant}" class="unavailableDesignError">
				<skapa-icon slot="message-icon" icon="warning-triangle"></skapa-icon>
				<span slot="body">${error}</span>
			</skapa-inline-message>
		`;
    }
    getHelpMessage(withTabs) {
        if (!this.helpMessage) {
            return null;
        }
        return html `<skapa-inline-message
			class="${withTabs ? 'helpMessageWithTabs' : 'helpMessageWithoutTabs'}"
			subtle
		>
			<skapa-icon slot="message-icon" icon="information-circle"></skapa-icon>
			<span slot="body">${this.helpMessage}</span>
		</skapa-inline-message>`;
    }
    onCardSizeChange({ detail }) {
        this.cardSize = detail === 0 ? DesignCardSizeEnum.big : DesignCardSizeEnum.small;
        this.requestUpdate();
    }
}
__decorate([
    property({ type: Object, required: true })
], OpenDesignCard.prototype, "theme", void 0);
__decorate([
    property({ type: Object })
], OpenDesignCard.prototype, "localizedInformation", void 0);
__decorate([
    property({ type: Object })
], OpenDesignCard.prototype, "translations", void 0);
__decorate([
    property({ type: Object })
], OpenDesignCard.prototype, "settings", void 0);
__decorate([
    property({ type: Boolean })
], OpenDesignCard.prototype, "loggedIn", void 0);
__decorate([
    property({ type: String })
], OpenDesignCard.prototype, "openDesignCodeState", void 0);
__decorate([
    property({ type: Object })
], OpenDesignCard.prototype, "savedDesigns", void 0);
__decorate([
    property({ type: String })
], OpenDesignCard.prototype, "openSavedDesignState", void 0);
__decorate([
    property({ type: String })
], OpenDesignCard.prototype, "variant", void 0);
__decorate([
    property({ type: String })
], OpenDesignCard.prototype, "helpMessage", void 0);
__decorate([
    property({ type: Boolean })
], OpenDesignCard.prototype, "enableTabs", void 0);
__decorate([
    property({ type: String })
], OpenDesignCard.prototype, "selectedTab", void 0);
//# sourceMappingURL=OpenDesignCard.js.map